import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Alert,
  Spinner,
} from 'react-bootstrap';
import reservationService from '../services/reservationService';
import { Reservation } from '../models/reserve';
import './Common.scss';

export default function ReserveCancelPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  const reserveId = searchParams.get('reserve_id');
  const expectedDate = searchParams.get('reserve_date');
  const expectedTime = searchParams.get('reserve_time');

  // 予約詳細を取得
  useEffect(() => {
    if (!reserveId || !expectedDate || !expectedTime) {
      setError(
        '必要なパラメータが不足しています。正しいURLでアクセスしてください。'
      );
      setLoading(false);
      return;
    }

    const fetchReservation = async () => {
      try {
        setLoading(true);
        const data = await reservationService.getReservation(
          parseInt(reserveId)
        );

        // 予約データとクエリパラメータの整合性を確認
        if (data.date !== expectedDate || data.time !== expectedTime) {
          console.error('不正アクセス検知:', {
            expected: { date: expectedDate, time: expectedTime },
            actual: { date: data.date, time: data.time },
          });
          setAccessDenied(true);
          setError(
            'アクセス権限がありません。正しいURLでアクセスしてください。'
          );
          setLoading(false);
          return;
        }

        setReservation(data);
      } catch (err) {
        console.error('予約取得エラー:', err);
        setError(
          '予約情報の取得に失敗しました。予約IDが正しいかご確認ください。'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [reserveId, expectedDate, expectedTime]);

  // キャンセル可能かどうかを判定
  const canCancel = (): boolean => {
    if (!reservation) return false;

    const now = new Date();
    const reserveDateTime = new Date(`${reservation.date}`);
    const timeDiff = reserveDateTime.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    return hoursDiff >= 24;
  };

  // 日付をフォーマットする関数
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short',
    });
  };

  // メニュー詳細を表示する関数
  const renderMenuDetails = (menuDetails: any): React.ReactNode => {
    if (!menuDetails || Object.keys(menuDetails).length === 0) {
      return <span className="text-muted">詳細なし</span>;
    }

    return (
      <div>
        {menuDetails.crystalHealing && (
          <div>
            <strong>クリスタルヒーリング:</strong>
            <br />
            セッション回数: {menuDetails.crystalHealing.sessions}回
            <br />
            施術部位: {menuDetails.crystalHealing.areas?.join(', ') || 'なし'}
          </div>
        )}
        {menuDetails.ensoficReiki && (
          <div>
            <strong>エンソフィックレイキ:</strong>
            <br />
            セッション種類: {menuDetails.ensoficReiki.sessionType}
            <br />
            ヒーリング種類: {menuDetails.ensoficReiki.healingType}
            {menuDetails.ensoficReiki.name && (
              <>
                <br />
                お名前: {menuDetails.ensoficReiki.name}
              </>
            )}
            {menuDetails.ensoficReiki.age && (
              <>
                <br />
                年齢: {menuDetails.ensoficReiki.age}歳
              </>
            )}
            {menuDetails.ensoficReiki.badHabits && (
              <>
                <br />
                改善したい悪癖: {menuDetails.ensoficReiki.badHabits}
              </>
            )}
          </div>
        )}
        {menuDetails.dnaActivation && (
          <div>
            <strong>DNAアクティベーション:</strong>
            <br />
            種類: {menuDetails.dnaActivation.type}
          </div>
        )}
        {menuDetails.sacredGeometry && (
          <div>
            <strong>神聖幾何学:</strong>
            <br />
            パターン: {menuDetails.sacredGeometry.pattern}
          </div>
        )}
      </div>
    );
  };

  // キャンセル処理
  const handleCancel = async () => {
    if (!reservation) return;

    setCancelling(true);
    try {
      await reservationService.deleteReservation(reservation.id);
      setCancelSuccess(true);
    } catch (err) {
      console.error('キャンセルエラー:', err);
      setError(
        '予約のキャンセルに失敗しました。お手数ですが、メールにてお問い合わせください。'
      );
    } finally {
      setCancelling(false);
    }
  };

  // ローディング中
  if (loading) {
    return (
      <Container fluid>
        <Row className="justify-content-center py-5">
          <Col xs={12} className="text-center">
            <Spinner animation="border" role="status" className="text-success">
              <span className="visually-hidden">読み込み中...</span>
            </Spinner>
            <div className="mt-3">予約情報を読み込み中...</div>
          </Col>
        </Row>
      </Container>
    );
  }

  // キャンセル成功
  if (cancelSuccess) {
    return (
      <Container fluid>
        <Row className="justify-content-center min-vh-100 g-0">
          <Col lg={8} md={8} sm={10} xs={12} className="mb-4">
            <Alert variant="success" className="text-center py-4">
              <h4>予約をキャンセルしました</h4>
              <p className="mb-3">
                予約のキャンセルが完了しました。
                <br />
                キャンセル確認メールをお送りしましたのでご確認ください。
              </p>
              <Button
                variant="success"
                onClick={() => navigate('/')}
                className="px-4"
              >
                トップページへ戻る
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  // エラー表示
  if (error || !reservation) {
    const isSecurityError = accessDenied || error?.includes('アクセス権限');

    return (
      <Container fluid>
        <Row className="justify-content-center min-vh-100 g-0">
          <Col lg={8} md={8} sm={10} xs={12} className="mb-4">
            <Alert
              variant={isSecurityError ? 'warning' : 'danger'}
              className="text-center py-4"
            >
              <h4>{isSecurityError ? '⚠️ 不正アクセス検知' : 'エラー'}</h4>
              <p className="mb-3">
                {error || '予約情報が見つかりません。'}
                {isSecurityError && (
                  <>
                    <br />
                    <small className="text-muted">
                      セキュリティのため、予約の詳細情報が一致しない場合はアクセスできません。
                    </small>
                  </>
                )}
              </p>
              <Button
                variant={isSecurityError ? 'outline-warning' : 'outline-danger'}
                onClick={() => navigate('/')}
                className="px-4"
              >
                トップページへ戻る
              </Button>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h2 className="mb-4 text-center text-warning white-stroke">
        予約キャンセル
      </h2>

      <Row className="justify-content-center">
        <Col lg={8} md={8} sm={10} xs={12} className="mb-4">
          {/* 予約内容の表示 */}
          <Card className="mb-4 border-warning">
            <Card.Header className="bg-warning text-dark">
              <h5 className="mb-0">予約内容確認</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>お名前:</strong> {reservation.name}
                  </p>
                  <p>
                    <strong>メールアドレス:</strong> {reservation.email}
                  </p>
                  <p>
                    <strong>予約日:</strong> {formatDate(reservation.date)}
                  </p>
                  <p>
                    <strong>予約時間:</strong> {reservation.time}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>メニュー:</strong> {reservation.menu}
                  </p>
                  <div className="mb-2">
                    <strong>メニュー詳細:</strong>
                    <div className="mt-1">
                      {renderMenuDetails(reservation.menuDetails)}
                    </div>
                  </div>
                  <p className="text-warning">
                    <strong>
                      合計金額: ¥{reservation.totalPrice.toLocaleString()}
                    </strong>
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* キャンセル可否の判定と操作 */}
          <Card
            className={`mb-4 ${canCancel() ? 'border-danger' : 'border-info'}`}
          >
            <Card.Header
              className={
                canCancel() ? 'bg-danger text-white' : 'bg-info text-white'
              }
            >
              <h5 className="mb-0">キャンセル処理</h5>
            </Card.Header>
            <Card.Body className="text-center">
              {canCancel() ? (
                <div>
                  <Alert variant="warning" className="mb-3">
                    <h6>予約キャンセルの確認</h6>
                    <p className="mb-0">
                      本当にこの予約をキャンセルしますか？
                      <br />
                      <strong>この操作は取り消せません。</strong>
                    </p>
                  </Alert>

                  <div className="d-flex justify-content-center gap-3">
                    <Button
                      variant="outline-secondary"
                      onClick={() => navigate('/')}
                      disabled={cancelling}
                      className="px-4"
                    >
                      やめる
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="px-4"
                    >
                      {cancelling ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          キャンセル中...
                        </>
                      ) : (
                        'キャンセルを確定する'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Alert variant="info" className="mb-3">
                    <h6>キャンセルについて</h6>
                    <p className="mb-0">
                      予約日の24時間前を過ぎているため、
                      <br />
                      このフォームからはキャンセルできません。
                      <br />
                      <strong>メールにてご連絡をお願いします。</strong>
                    </p>
                  </Alert>

                  <div className="d-flex justify-content-center gap-3">
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate('/contact')}
                      className="px-4"
                    >
                      お問い合わせフォームへ
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/')}
                      className="px-4"
                    >
                      トップページへ戻る
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
